import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ProductsRoutePending } from "@/components/catalogue/CatalogueRouteSkeletons";
import { CatalogueRouteError } from "@/components/catalogue/CatalogueRouteError";
import { useEffect, useMemo } from "react";
import { ProductCard } from "@/components/ProductCard";
import { listProducts, getCategoryBySlug, listCategories } from "@/lib/catalog.functions";
import { useT, pickLocalized } from "@/i18n/LanguageProvider";
import { track } from "@/lib/analytics-client";
import { ShieldCheck, Wallet, PackageCheck, Truck, RefreshCw, ChevronRight } from "lucide-react";
import { bilingualTitle, bilingualDesc, hreflangLinks } from "@/lib/seo";
import { getPageSeoOverride, faqOverrideJsonLd } from "@/lib/page-seo.functions";
import { EditSeoShortcut } from "@/components/seo/EditSeoShortcut";

import { CommercialFAQ } from "@/components/CommercialFAQ";
import { PopularGuidesStrip } from "@/components/PopularGuidesStrip";
import { CATEGORY_FAQS, faqPageLd } from "@/lib/commercial-faqs";
import { guidesForCategory } from "@/lib/guides-for-category";
import { brandsWithCategory } from "@/data/brand-categories";
import { bnSafe, hasBengali } from "@/lib/typography";
import heroPartsImg from "@/assets/hero-parts.jpg";
import { AnswerBlock } from "@/components/seo/AnswerBlock";
import { formatBDT } from "@/lib/format";
import { toBnDigits } from "@/lib/format-bn";
import { formatFreeDeliveryThreshold } from "@/lib/free-delivery";

const DISPLAY = "font-[family-name:var(--font-display)]";

export const Route = createFileRoute("/c/$slug/")({
  pendingComponent: ProductsRoutePending,
  loader: async ({ params, context }) => {
    const [cat, products, categories, seoOverride] = await Promise.all([
      context.queryClient.fetchQuery({
        queryKey: ["cat", params.slug],
        queryFn: () => getCategoryBySlug({ data: { slug: params.slug } }),
      }),
      context.queryClient.fetchQuery({
        queryKey: ["cat-products", params.slug],
        queryFn: () => listProducts({ data: { category: params.slug } as any }),
      }),
      context.queryClient.fetchQuery({
        queryKey: ["categories"],
        queryFn: () => listCategories(),
        staleTime: 5 * 60_000,
      }),
      context.queryClient
        .fetchQuery({
          queryKey: ["seo-override", `/c/${params.slug}`],
          queryFn: () => getPageSeoOverride({ data: { path: `/c/${params.slug}` } }),
          staleTime: 60_000,
        })
        .catch(() => null),
    ]);
    if (!cat) throw notFound();
    return { cat, products: products ?? [], categories: categories ?? [], seoOverride };
  },
  head: ({ loaderData, params }) => {
    const cat = loaderData?.cat;
    const products = loaderData?.products ?? [];
    const name = cat?.name_en ?? "Category";
    const nameBn = cat?.name_bn ?? null;
    const title = bilingualTitle(`${name} — Motorcycle & Car Parts in Bangladesh`, nameBn);
    const desc = bilingualDesc(
      `Shop ${name.toLowerCase()} for motorcycles, cars, CNG, and trucks in Bangladesh. Genuine OEM and aftermarket options from verified sellers. Cash on delivery nationwide.`,
      nameBn,
    );
    const url = `https://jantrobazar.com/c/${params.slug}`;
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://jantrobazar.com/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "Products",
          item: "https://jantrobazar.com/products",
        },
        { "@type": "ListItem", position: 3, name, item: url },
      ],
    };
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${name} — Jantro Bazar`,
      inLanguage: ["en", "bn"],
      numberOfItems: products.length,
      itemListElement: products.slice(0, 20).map((p: any, i: number) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://jantrobazar.com/products/${p.slug}`,
        name: p.title_en,
      })),
    };
    // Price band across the listings actually shown on this page — gives
    // Google and AI shopping answers a concrete BDT range for
    // "<category> price in Bangladesh" queries.
    const livePrices = products
      .map((p: any) => Number(p.discount_price ?? p.price))
      .filter((n: number) => Number.isFinite(n) && n > 0);
    const collectionPage: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${name}${nameBn ? ` · ${nameBn}` : ""} — Jantro Bazar`,
      url,
      inLanguage: ["en", "bn"],
      isPartOf: { "@type": "WebSite", name: "Jantro Bazar", url: "https://jantrobazar.com/" },
      about: { "@type": "Thing", name },
      // Geographic signal: every listing here ships inside Bangladesh.
      spatialCoverage: { "@type": "Country", name: "Bangladesh" },
      ...(livePrices.length
        ? {
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "BDT",
              lowPrice: Math.min(...livePrices),
              highPrice: Math.max(...livePrices),
              offerCount: livePrices.length,
              availability: "https://schema.org/InStock",
              areaServed: { "@type": "Country", name: "Bangladesh" },
            },
          }
        : {}),
    };

    const faq = faqPageLd(CATEGORY_FAQS(name, nameBn));
    const ogImage = `https://jantrobazar.com${heroPartsImg}`;
    const override: any = (loaderData as any)?.seoOverride ?? null;
    const finalCanonical = override?.canonical_url || url;
    const finalTitle = override?.og_title || title;
    const finalDesc = override?.og_description || desc;
    const overrideFaq = faqOverrideJsonLd(override?.faq);
    return {
      meta: [
        { title: finalTitle },
        { name: "description", content: finalDesc },
        // An empty category is a thin page: keep it crawlable for link
        // discovery but out of the index until it has real listings.
        ...(override?.robots
          ? [{ name: "robots", content: override.robots }]
          : products.length === 0
            ? [{ name: "robots", content: "noindex, follow" }]
            : []),
        { property: "og:title", content: finalTitle },
        { property: "og:description", content: finalDesc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: finalCanonical },
        { property: "og:site_name", content: "Jantro Bazar" },
        { property: "og:image", content: ogImage },
        { property: "og:image:alt", content: `${name} — Jantro Bazar` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: finalTitle },
        { name: "twitter:description", content: finalDesc },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: finalCanonical }, ...hreflangLinks(finalCanonical)],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(collectionPage) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
        { type: "application/ld+json", children: JSON.stringify(itemList) },
        { type: "application/ld+json", children: JSON.stringify(overrideFaq ?? faq) },
      ],
    };
  },
  errorComponent: CatalogueRouteError,
  notFoundComponent: () => <div className="p-16 text-center">Category not found.</div>,
  component: CategoryPage,
});

function CategoryPage() {
  const { cat, products, categories } = Route.useLoaderData();
  const { t, lang } = useT();

  useEffect(() => {
    track("view_category", { category_slug: cat.slug, category_name: cat.name_en });
  }, [cat.slug, cat.name_en]);

  const brands = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const p of products as any[]) {
      const b = (p.brand ?? "").trim();
      if (!b || seen.has(b.toLowerCase())) continue;
      seen.add(b.toLowerCase());
      out.push(b);
      if (out.length >= 12) break;
    }
    return out;
  }, [products]);

  const relatedCats = useMemo(() => {
    return (categories as any[]).filter((c) => !c.parent_id && c.slug !== cat.slug).slice(0, 8);
  }, [categories, cat.slug]);

  const inStockCount = useMemo(
    () => (products as any[]).filter((p) => (p.stock ?? 0) > 0 || p.in_stock !== false).length,
    [products],
  );

  // Live BDT band for the answer block — same numbers that back the
  // AggregateOffer in this route's structured data.
  const priceBand = useMemo(() => {
    const prices = (products as any[])
      .map((p) => Number(p.discount_price ?? p.price))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!prices.length) return null;
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [products]);


  const avgRating = useMemo(() => {
    const withRating = (products as any[])
      .map((p) => Number(p.rating ?? 0))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!withRating.length) return null;
    return withRating.reduce((a, b) => a + b, 0) / withRating.length;
  }, [products]);

  const trackCrumb = (label: string, href: string) =>
    track("breadcrumb_click", { label, href, from: `/c/${cat.slug}` });

  const localName = pickLocalized({ name_en: cat.name_en, name_bn: cat.name_bn }, lang);

  return (
    <div>
      <main className="flex-1 bg-[color:var(--color-ink)] text-white">
        <div className="container-app py-8 md:py-12">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="text-xs md:text-sm text-white/50">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link
                  to="/"
                  className="hover:text-[color:var(--color-ember)] transition"
                  onClick={() => trackCrumb("Home", "/")}
                >
                  Home
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="h-3 w-3 inline text-white/30" />
              </li>
              <li>
                <Link
                  to="/products"
                  className="hover:text-[color:var(--color-ember)] transition"
                  onClick={() => trackCrumb("Products", "/products")}
                >
                  {t("nav.products")}
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="h-3 w-3 inline text-white/30" />
              </li>
              <li className="text-white font-semibold">{localName}</li>
            </ol>
          </nav>

          {/* Editorial hero */}
          <header className="mt-6 md:mt-10 grid md:grid-cols-[1.4fr_1fr] gap-8 md:gap-12 items-end pb-10 border-b border-white/10">
            <div className="min-w-0">
              <div className="text-[11px] font-extrabold uppercase tracking-[0.24em] mb-3">
                <span className="text-[color:var(--color-ember)]">CATEGORY</span>
                <span className="text-white/40"> / {(cat.name_en ?? "").toUpperCase()}</span>
              </div>
              <h1
                className={`${DISPLAY} text-4xl md:text-6xl font-extrabold ${bnSafe(localName, "tracking-tight")} leading-[1.02] text-white`}
              >
                {localName}
              </h1>
              <div
                className="h-[3px] w-16 bg-[color:var(--color-ember)] rounded-full mt-5"
                aria-hidden
              />
              <p className="mt-5 text-sm md:text-base text-white/60 max-w-xl leading-relaxed">
                {lang === "bn"
                  ? `বাংলাদেশে ${cat.name_bn ?? cat.name_en} — যাচাইকৃত বিক্রেতাদের কাছ থেকে জেনুইন ও আফটারমার্কেট অপশন, সাথে সারাদেশে ক্যাশ অন ডেলিভারি।`
                  : `Browse ${cat.name_en.toLowerCase()} from verified sellers across Bangladesh — genuine and aftermarket options with nationwide cash on delivery.`}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <TrustChip
                  icon={<ShieldCheck className="h-3.5 w-3.5" />}
                  label={lang === "bn" ? "যাচাইকৃত বিক্রেতা" : "Verified sellers"}
                />
                <TrustChip
                  icon={<Wallet className="h-3.5 w-3.5" />}
                  label={lang === "bn" ? "সারাদেশে COD" : "COD nationwide"}
                />
                <TrustChip
                  icon={<RefreshCw className="h-3.5 w-3.5" />}
                  label={lang === "bn" ? "রিটার্ন সুরক্ষা" : "Return window"}
                />
              </div>
            </div>
            <dl className="grid grid-cols-3 gap-4 md:gap-6">
              <Stat value={String(inStockCount)} label={lang === "bn" ? "স্টকে আছে" : "In stock"} />
              <Stat value={String(brands.length)} label={lang === "bn" ? "ব্র্যান্ড" : "Brands"} />
              <Stat
                value={avgRating ? avgRating.toFixed(1) : "—"}
                label={lang === "bn" ? "গড় রেটিং" : "Avg. rating"}
              />
            </dl>

            {products.length >= 3 ? (
              <Link
                to="/c/$slug/price"
                params={{ slug: cat.slug }}
                className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-white/25 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
              >
                {lang === "bn"
                  ? `${cat.name_bn ?? cat.name_en}-এর দাম দেখুন`
                  : `${cat.name_en} price guide`}
                <ChevronRight className="h-4 w-4" aria-hidden />
              </Link>
            ) : null}
          </header>

          {/* Quick answer — factual, built from the listings on this page. */}
          {priceBand ? (
            <AnswerBlock
              question={
                lang === "bn"
                  ? `বাংলাদেশে ${localName}-এর দাম কত?`
                  : `How much do ${cat.name_en.toLowerCase()} cost in Bangladesh?`
              }
              answer={
                lang === "bn"
                  ? `এই মুহূর্তে যন্ত্র বাজারে ${localName} ${formatBDT(priceBand.min, "bn")} থেকে ${formatBDT(priceBand.max, "bn")} দামে পাওয়া যাচ্ছে, ${toBnDigits(String(inStockCount))}টি আইটেম স্টকে আছে। সব দাম টাকায়, সারাদেশে ক্যাশ অন ডেলিভারি এবং ৳${formatFreeDeliveryThreshold("bn")} টাকার বেশি অর্ডারে ফ্রি ডেলিভারি।`
                  : `${cat.name_en} on Jantro Bazar currently range from ${formatBDT(priceBand.min)} to ${formatBDT(priceBand.max)}, with ${inStockCount} item${inStockCount === 1 ? "" : "s"} in stock from verified sellers. Prices are in Bangladeshi Taka, cash on delivery is available nationwide, and orders above ৳${formatFreeDeliveryThreshold("en")} ship free.`
              }
              facts={[
                {
                  label: lang === "bn" ? "দামের রেঞ্জ" : "Price range",
                  value: `${formatBDT(priceBand.min, lang)} – ${formatBDT(priceBand.max, lang)}`,
                },
                {
                  label: lang === "bn" ? "স্টকে" : "In stock",
                  value:
                    lang === "bn"
                      ? `${toBnDigits(String(inStockCount))} আইটেম`
                      : `${inStockCount} item${inStockCount === 1 ? "" : "s"}`,
                },
                {
                  label: lang === "bn" ? "ডেলিভারি" : "Delivery",
                  value: lang === "bn" ? "১–৫ দিন, সারাদেশে" : "1–5 days, nationwide",
                },
                {
                  label: lang === "bn" ? "পেমেন্ট ও রিটার্ন" : "Payment & returns",
                  value: lang === "bn" ? "ক্যাশ অন ডেলিভারি · ৭ দিন" : "Cash on delivery · 7 days",
                },
              ]}
              footnote={
                lang === "bn"
                  ? "দাম এই পেজে থাকা লাইভ লিস্টিং থেকে নেওয়া এবং বিক্রেতাভেদে বদলাতে পারে।"
                  : "Range reflects the live listings on this page and varies by seller and condition."
              }
            />
          ) : null}



          {/* Brand rail — prefer seeded /brand/{slug}/{cat} deep pages */}
          {(() => {
            const seeded = brandsWithCategory(cat.slug);
            const seededByLower = new Map(seeded.map((s) => [s.brand_name.toLowerCase(), s]));
            const seededSlugSeen = new Set<string>();
            // Product-derived brands first (in order), then any remaining seeded brands.
            const merged: Array<{ label: string; href: string; slug?: string }> = [];
            for (const b of brands) {
              const s = seededByLower.get(b.toLowerCase());
              if (s) {
                seededSlugSeen.add(s.brand_slug);
                merged.push({
                  label: b,
                  href: `/brand/${s.brand_slug}/${cat.slug}`,
                  slug: s.brand_slug,
                });
              } else {
                merged.push({
                  label: b,
                  href: `/products?category=${encodeURIComponent(cat.slug)}&brand=${encodeURIComponent(b)}`,
                });
              }
            }
            for (const s of seeded) {
              if (seededSlugSeen.has(s.brand_slug)) continue;
              merged.push({
                label: lang === "bn" ? s.brand_name_bn : s.brand_name,
                href: `/brand/${s.brand_slug}/${cat.slug}`,
                slug: s.brand_slug,
              });
            }
            if (merged.length === 0) return null;
            return (
              <section className="mt-10">
                <div className="text-[11px] font-extrabold uppercase tracking-[0.24em] mb-4">
                  <span className="text-[color:var(--color-ember)]">01</span>
                  <span className="text-white/40">
                    {" "}
                    / {lang === "bn" ? "ব্র্যান্ড" : "SHOP BY BRAND"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {merged.map((b) => {
                    const isBn = hasBengali(b.label);
                    const chipClass = `group inline-flex items-center gap-2 rounded-full border border-white/10 bg-[color:var(--color-graphite)] px-4 h-9 text-xs font-semibold ${isBn ? "" : "uppercase tracking-wider"} text-white/80 hover:border-[color:var(--color-ember)]/60 hover:text-white transition`;
                    const dot = (
                      <span
                        className="h-1.5 w-1.5 rounded-full bg-[color:var(--color-ember)] group-hover:scale-125 transition"
                        aria-hidden
                      />
                    );
                    return b.slug ? (
                      <Link
                        key={b.slug}
                        to="/brand/$slug/$category"
                        params={{ slug: b.slug, category: cat.slug }}
                        className={chipClass}
                      >
                        {dot}
                        {b.label}
                      </Link>
                    ) : (
                      <Link
                        key={b.label}
                        to="/products"
                        search={{ category: cat.slug, brand: b.label } as any}
                        className={chipClass}
                      >
                        {dot}
                        {b.label}
                      </Link>
                    );
                  })}
                </div>
              </section>
            );
          })()}

          {/* Product grid */}
          <section className="mt-12">
            <div className="flex items-end justify-between mb-6 gap-4">
              <div className="min-w-0">
                <div className="text-[11px] font-extrabold uppercase tracking-[0.24em] mb-2">
                  <span className="text-[color:var(--color-ember)]">02</span>
                  <span className="text-white/40"> / {lang === "bn" ? "পার্টস" : "PARTS"}</span>
                </div>
                <h2
                  className={`${DISPLAY} text-2xl md:text-3xl font-extrabold ${lang === "bn" ? "" : "tracking-tight"} text-white`}
                >
                  {lang === "bn" ? "সব লিস্টিং" : "All listings"}
                </h2>
                <div
                  className="h-[3px] w-12 bg-[color:var(--color-ember)] rounded-full mt-3"
                  aria-hidden
                />
              </div>
              <span className="shrink-0 text-xs text-white/50 font-semibold tabular-nums">
                {(products as any[]).length} {lang === "bn" ? "টি পার্টস" : "items"}
              </span>
            </div>
            {(products as any[]).length > 0 ? (
              <div
                id="category-grid"
                role="region"
                aria-live="polite"
                aria-label={lang === "bn" ? "পার্টস তালিকা" : "Parts listings"}
                className="rounded-2xl bg-card p-4 md:p-6"
              >
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {(products as any[]).map((p: any) => (
                    <ProductCard key={p.id} p={p} />
                  ))}
                </div>
              </div>
            ) : (
              <div
                id="category-grid"
                role="status"
                aria-live="polite"
                className="text-center py-16 rounded-2xl border border-dashed border-white/15 text-white/50"
              >
                <p className="text-white/70 font-semibold">
                  {lang === "bn"
                    ? "এই ক্যাটাগরিতে এখনো কোনো পার্টস নেই।"
                    : "No products yet in this category."}
                </p>
                <p className="mt-1 text-sm text-white/45">
                  {lang === "bn"
                    ? "আপনার গাড়ির মডেল ও পার্টের নাম পাঠান — আমরা বিক্রেতা খুঁজে দাম জানিয়ে দেব।"
                    : "Send your vehicle model and the part name — we'll find a seller and quote you back."}
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  <a
                    href={`https://wa.me/8801811600591?text=${encodeURIComponent(
                      lang === "bn"
                        ? `আমি ${localName} খুঁজছি। গাড়ি: `
                        : `I'm looking for ${cat.name_en}. Vehicle: `,
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 min-h-11 px-4 rounded-lg bg-[color:var(--color-ember)] text-sm font-bold text-accent-foreground hover:opacity-90"
                  >
                    {lang === "bn" ? "এই পার্টের জন্য রিকোয়েস্ট করুন" : "Request this part"}
                  </a>
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-1.5 min-h-11 px-4 rounded-lg border border-white/20 text-sm font-semibold text-white/80 hover:border-[color:var(--color-ember)]/60"
                  >
                    {lang === "bn" ? "সব পার্টস দেখুন" : "Browse all parts"}
                  </Link>
                </div>
              </div>
            )}
          </section>

          {/* Related categories */}
          {relatedCats.length > 0 && (
            <section className="mt-14">
              <div className="text-[11px] font-extrabold uppercase tracking-[0.24em] mb-4">
                <span className="text-[color:var(--color-ember)]">03</span>
                <span className="text-white/40"> / {lang === "bn" ? "সম্পর্কিত" : "RELATED"}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {relatedCats.map((c: any) => (
                  <Link
                    key={c.id}
                    to="/c/$slug"
                    params={{ slug: c.slug }}
                    className="group rounded-xl border border-white/10 bg-[color:var(--color-graphite)] p-4 hover:border-[color:var(--color-ember)]/60 transition"
                  >
                    <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[color:var(--color-ember)] mb-1">
                      <span>Browse</span>
                      <ChevronRight
                        className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5"
                        aria-hidden
                      />
                    </div>
                    <div
                      className={`${DISPLAY} text-base md:text-lg font-bold text-white leading-snug`}
                    >
                      {pickLocalized({ name_en: c.name_en, name_bn: c.name_bn }, lang)}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div
            className="mt-14 h-px bg-gradient-to-r from-transparent via-[color:var(--color-ember)]/25 to-transparent"
            aria-hidden
          />

          {/* About + How-it-works */}
          <section className="mt-14 grid md:grid-cols-[1.2fr_1fr] gap-8 md:gap-12">
            <div>
              <div className="text-[11px] font-extrabold uppercase tracking-[0.24em] mb-3">
                <span className="text-[color:var(--color-ember)]">04</span>
                <span className="text-white/40"> / {lang === "bn" ? "সম্পর্কে" : "ABOUT"}</span>
              </div>
              <h2
                className={`${DISPLAY} text-2xl md:text-3xl font-extrabold ${lang === "bn" ? "" : "tracking-tight"} text-white`}
              >
                {lang === "bn"
                  ? `${cat.name_bn ?? cat.name_en} — কেন Jantro Bazar`
                  : `${cat.name_en} on Jantro Bazar`}
              </h2>
              <p className="mt-4 text-sm md:text-base text-white/60 leading-relaxed max-w-xl">
                {lang === "bn"
                  ? `Jantro Bazar-এ ${cat.name_bn ?? cat.name_en} সরাসরি যাচাইকৃত বিক্রেতাদের কাছ থেকে আসে। প্রতিটি লিস্টিং-এ কন্ডিশন, ওয়ারেন্টি এবং ফিটমেন্ট তথ্য দেখানো হয়, যাতে আপনি বাইক, গাড়ি, CNG বা ট্রাকের জন্য সঠিক পার্টস আত্মবিশ্বাসের সাথে অর্ডার করতে পারেন — সাথে সারাদেশে ক্যাশ অন ডেলিভারি।`
                  : `${cat.name_en} on Jantro Bazar come from verified sellers across Bangladesh. Every listing shows condition, warranty and fitment so you can order the right part for your motorcycle, car, CNG or truck with confidence — backed by nationwide cash on delivery and a clear return window.`}
              </p>
              <div className="mt-5 flex flex-wrap gap-4 text-xs">
                <Link
                  to="/guarantee"
                  className="text-white/70 underline decoration-[color:var(--color-ember)]/40 underline-offset-4 hover:text-white transition"
                >
                  {lang === "bn" ? "বায়ার গ্যারান্টি" : "Buyer guarantee"}
                </Link>
                <Link
                  to="/returns"
                  className="text-white/70 underline decoration-[color:var(--color-ember)]/40 underline-offset-4 hover:text-white transition"
                >
                  {lang === "bn" ? "রিটার্ন পলিসি" : "Return policy"}
                </Link>
                <Link
                  to="/parts-guide"
                  className="text-white/70 underline decoration-[color:var(--color-ember)]/40 underline-offset-4 hover:text-white transition"
                >
                  {lang === "bn" ? "পার্টস গাইড" : "Parts guide"}
                </Link>
              </div>
            </div>
            <ol className="space-y-3">
              <Step
                n="01"
                icon={<PackageCheck className="h-4 w-4" />}
                title={lang === "bn" ? "অর্ডার" : "Order"}
                body={
                  lang === "bn"
                    ? "সঠিক ফিটমেন্ট দেখে অর্ডার করুন — COD সহ।"
                    : "Pick the right fitment, pay cash on delivery."
                }
              />
              <Step
                n="02"
                icon={<Truck className="h-4 w-4" />}
                title={lang === "bn" ? "ডেলিভারি" : "Ship"}
                body={
                  lang === "bn" ? "সারাদেশে ২–৪ দিনে ডেলিভারি।" : "Nationwide 2–4 day delivery."
                }
              />
              <Step
                n="03"
                icon={<RefreshCw className="h-4 w-4" />}
                title={lang === "bn" ? "ফিট অথবা রিফান্ড" : "Fit-or-refund"}
                body={
                  lang === "bn"
                    ? "ফিট না হলে রিটার্ন উইন্ডোর মধ্যে ফেরত।"
                    : "Doesn't fit? Return within the window."
                }
              />
            </ol>
          </section>
        </div>

        {/* Buyer-knowledge cross-links + FAQ (light band, semantic tokens) */}
        <div className="bg-background text-foreground">
          <div className="container-app py-12 md:py-16 space-y-14">
            <PopularGuidesStrip
              items={guidesForCategory(cat.slug, 6)}
              headingEn={`Buyer guides — ${cat.name_en}`}
              headingBn={`ক্রেতা গাইড — ${cat.name_bn ?? cat.name_en}`}
            />
            <CommercialFAQ
              items={CATEGORY_FAQS(cat.name_en, cat.name_bn ?? null)}
              headingEn={`${cat.name_en} — frequently asked questions`}
              headingBn={`${cat.name_bn ?? cat.name_en} — প্রায়শই জিজ্ঞাসিত প্রশ্ন`}
            />
          </div>
        </div>
      </main>
      <EditSeoShortcut path={`/c/${cat.slug}`} />
    </div>
  );
}

function TrustChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-card/5 px-3 h-7 text-[11px] font-semibold text-white/80">
      <span className="text-[color:var(--color-ember)]">{icon}</span>
      {label}
    </span>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <dd
        className={`${DISPLAY} text-3xl md:text-5xl font-extrabold text-white leading-none tabular-nums`}
      >
        {value}
      </dd>
      <dt className="mt-2 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.14em] text-white/50">
        {label}
      </dt>
    </div>
  );
}

function Step({
  n,
  icon,
  title,
  body,
}: {
  n: string;
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <li className="flex gap-4 rounded-xl border border-white/10 bg-[color:var(--color-graphite)] p-4">
      <div className="shrink-0 flex flex-col items-center gap-1 pt-0.5">
        <span
          className={`${DISPLAY} text-lg font-extrabold text-[color:var(--color-ember)] leading-none`}
        >
          {n}
        </span>
        <span className="text-white/60">{icon}</span>
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold text-white">{title}</div>
        <div className="mt-1 text-xs text-white/60 leading-relaxed">{body}</div>
      </div>
    </li>
  );
}
